/**
 * BRITA GA4 tracking (theme side)
 * -------------------------------------------------------------------------
 * Pushes GA4 events onto the storefront `window.dataLayer`, where the GTM
 * container reads them.
 *
 * Payload shape:
 *   page_meta -> { event: 'page_meta', ...data }           (flat)
 *   others    -> { event: 'gx.<name>', event_name: <name>, <name>: data }
 *
 * Public API:
 *   window.BritaGx.track('cta_click', { cta_text: 'Get a Quote', ... });
 *
 * Declarative API (no JS needed):
 *   <a href="#" data-gx-event="cta_click"
 *      data-gx-cta-text="Get a Quote" data-gx-cta-location="homepage_hero">…</a>
 *   Multi-word params can also be passed as JSON via data-gx-params='{…}'.
 *
 * Click-driven events fire on pointerdown/pointerup/keydown rather than click,
 * so a link's navigation cannot cut off the GA4 request (see "Activation
 * tracking"). Each activation is pushed exactly once. Inside a draggable
 * carousel the press waits for pointerup and a drag is dropped (see "Drag
 * guard") - a swipe opens nothing, so it is not a selection.
 */


(function () {
  'use strict';

  // Logs every pushed event to the console when window.BritaGxConfig.debug is true.
  var DEBUG = !!(window.BritaGxConfig && window.BritaGxConfig.debug);

  function buildPayload(name, data) {
    if (name === 'page_meta') {
      return Object.assign({ event: 'page_meta' }, data || {});
    }
    var payload = { event: 'gx.' + name, event_name: name };
    payload[name] = data || {};
    return payload;
  }

  /**
   * Push a GA4 event onto the storefront dataLayer.
   * @param {string} name  event name, e.g. 'cta_click' or 'page_meta'
   * @param {object} [data] event payload
   */
  function track(name, data) {
    if (!name) return;
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(buildPayload(name, data));
      if(DEBUG && window.console && console.log) {
        console.log('[BritaGx] pushed "' + name + '"', data, window.dataLayer);
      }
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('[BritaGx] push failed for "' + name + '"', err);
      }
    }
  }

  // Ecommerce event names pushed WITHOUT the `eec.` prefix (the bare event name
  // is used as the dataLayer `event`). Every other ecommerce event gets `eec.`.
  var EC_NO_PREFIX = {
    add_coupon: true,
    add_coupon_error: true,
    activate_coupon_field: true
  };

  /**
   * Push an ecommerce event onto the storefront dataLayer.
   * @param {string} name  e.g. 'select_item'
   * @param {object} ecommerce  the ecommerce payload (items / promotion / coupon …)
   */
  function trackEcommerce(name, ecommerce) {
    if (!name) return;
    try {
      window.dataLayer = window.dataLayer || [];
      var payload = {
        event: EC_NO_PREFIX[name] ? name : 'eec.' + name,
        event_name: name,
        ecommerce: ecommerce || {},
        event_source: 'source_code'
      };
      window.dataLayer.push(payload);
      if(DEBUG && window.console && console.log) {
        console.log('[BritaGx] pushed "' + name + '"', payload, window.dataLayer);
      }
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('[BritaGx] ecommerce push failed for "' + name + '"', err);
      }
    }
  }

  // Reads the promotion fields from a data-gx-promotion element.
  function promotionData(el) {
    return {
      creative_name: el.getAttribute('data-gx-creative-name') || '',
      creative_slot: el.getAttribute('data-gx-creative-slot') || '',
      promotion_id: el.getAttribute('data-gx-promotion-id') || '',
      promotion_name: el.getAttribute('data-gx-promotion-name') || ''
    };
  }

  // ---- Declarative click tracking ---------------------------------------

  // Converts a data-gx-* attribute suffix to a snake_case param key
  // (data-gx-cta-text -> cta_text, data-gx-view-mode -> view_mode).
  function toSnake(key) {
    return key.replace(/-/g, '_');
  }

  // data-gx-* suffixes that are control hooks, not payload params; skipped by
  // collectParams.
  var GX_SKIP_KEYS = {
    event: 1,
    params: 1,
    'toggle-event': 1,
    'focus-event': 1,
    'submit-event': 1,
    'interaction-event': 1,
    'step-event': 1,
    nav: 1,
    'nav-element': 1,
    'swipe-content': 1
  };

  // Builds the event params from an element's data-gx-params JSON and its
  // data-gx-* attributes (attributes override JSON on key collision).
  function collectParams(el) {
    var params = {};
    var json = el.getAttribute('data-gx-params');
    if (json) {
      try {
        params = JSON.parse(json);
      } catch (e) {
        /* ignore malformed JSON, fall back to attributes */
      }
    }
    var attrs = el.attributes;
    for (var i = 0; i < attrs.length; i += 1) {
      var attr = attrs[i];
      if (attr.name.indexOf('data-gx-') !== 0) continue;
      var key = attr.name.slice('data-gx-'.length);
      if (GX_SKIP_KEYS[key]) continue;
      params[toSnake(key)] = attr.value;
    }
    return params;
  }

  // item_list_id must always reach GA4 as a string, never a number - a numeric
  // list id (e.g. collection.id) would otherwise split the reports by type.
  // Liquid already coerces it; this is the guarantee for any hand-written payload.
  function normalizeListId(payload) {
    if (payload.item_list_id != null && payload.item_list_id !== '') {
      payload.item_list_id = String(payload.item_list_id);
    }
    (payload.items || []).forEach(function (item) {
      if (item && item.item_list_id != null && item.item_list_id !== '') {
        item.item_list_id = String(item.item_list_id);
      }
    });
  }

  // Parses and caches the element's data-gx-ec-payload JSON on the element.
  function ecPayload(el) {
    if (el._gxEcPayload) return el._gxEcPayload;
    var parsed = {};
    try {
      parsed = JSON.parse(el.getAttribute('data-gx-ec-payload') || '{}');
    } catch (e) {
      /* ignore malformed payload */
    }
    normalizeListId(parsed);
    el._gxEcPayload = parsed;
    return parsed;
  }

  var DOWNLOAD_RE =
    /\.(pdf|docx?|xlsx?|pptx?|zip|rar|7z|csv|txt|rtf|dmg|exe|key|pages|numbers)(\?|#|$)/i;

  // Returns the lowercase file extension of a URL.
  function fileExtension(url) {
    var clean = (url || '').split('?')[0].split('#')[0];
    var match = clean.match(/\.([a-z0-9]+)$/i);
    return match ? match[1].toLowerCase() : '';
  }

  // A host can require that the activation was a link to its own product, via
  // data-gx-product-path. A product card carries the select_item payload on its
  // wrapper so that every product link counts (image, title, "choose options",
  // CTA) - but the vendor/type links inside the same card lead to collections,
  // and the quick add form is not a selection either. Hosts without the
  // attribute keep the plain "anything inside me" behaviour.
  function ecActivationCounts(host, target) {
    var path = host.getAttribute('data-gx-product-path');
    if (!path) return true;
    var link = target.closest ? target.closest('a[href]') : null;
    return !!link && link.pathname === path;
  }

  // Returns the current page_type from page_meta.
  function currentPageType() {
    return (
      (window.BritaGxConfig &&
        window.BritaGxConfig.page_meta &&
        window.BritaGxConfig.page_meta.page_type) ||
      ''
    );
  }

  // ---- navigation: only real navigations ----------------------------------
  // The menus are full of `a[href]` and `summary[data-link]` elements that do
  // not navigate. `navigation` must fire for the ones that do, and only those.

  // Hrefs that never leave the page - dropdown and locale toggles use these
  // (e.g. the country selector's `<a href="#">` in the mobile drawer).
  function isPlaceholderHref(href) {
    return !href || href.charAt(0) === '#' || /^javascript:/i.test(href);
  }

  // Mirrors FoxTheme.config.isTouch (assets/theme.js); recomputed only when the
  // theme bundle has not defined it.
  function isTouchDevice() {
    if (window.FoxTheme && FoxTheme.config && typeof FoxTheme.config.isTouch === 'boolean') {
      return FoxTheme.config.isTouch;
    }
    var hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    return hasTouch && !window.matchMedia('(pointer: fine)').matches;
  }

  // The URL a <summary> navigates to, or '' when the click only toggles the
  // dropdown. handleSummaryClick (assets/header.js) always preventDefaults and
  // then follows `data-link` for exactly one case: non-touch + trigger="hover".
  // On touch, and with trigger="click", the panel just opens.
  function summaryNavigatesTo(summary) {
    var url = summary.getAttribute('data-link') || '';
    if (isPlaceholderHref(url)) return '';
    var details = summary.parentElement;
    if (!details || details.tagName !== 'DETAILS') return '';
    if (details.getAttribute('trigger') !== 'hover') return '';
    return isTouchDevice() ? '' : url;
  }

  // Stable key for navigation_element. `data-gx-nav-element` is the explicit,
  // language-independent name; without it the link text is used, which is
  // localised. Both are normalised to lower snake_case (water_dispenser).
  function navElementName(el) {
    var raw = el.getAttribute('data-gx-nav-element') || el.textContent || '';
    return raw
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '') // strip diacritics: Zubehoer -> Zubehor
      .replace(/ß/g, 'ss') // eszett has no decomposition
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 100);
  }

  function trackNavigation(bar, el, url) {
    track('navigation', {
      navigation_bar: bar.getAttribute('data-gx-nav'),
      navigation_element: navElementName(el),
      link_url: url
    });
  }

  // ---- Activation tracking (click-driven events, fired before navigation) --
  // A link click races its own navigation: dataLayer.push is synchronous, but
  // GTM processes the push asynchronously, so the unload can kill the GA4
  // request before the tag fires. Click-driven events therefore run on
  // pointerdown (mouse) / pointerup (touch, pen) / keydown (Enter, Space) -
  // all of which happen before the navigation starts - and the click listener
  // only serves as a fallback for activations the pointer path cannot see
  // (synthetic clicks, no Pointer Events support). Nothing is deferred and no
  // navigation is delayed.
  function handleActivation(target) {
    // Custom click event from data-gx-event.
    var tracked = target.closest ? target.closest('[data-gx-event]') : null;
    if (tracked) {
      track(tracked.getAttribute('data-gx-event'), collectParams(tracked));
    }

    // Ecommerce click event from data-gx-ec-event (e.g. select_item on cards).
    var ec = target.closest ? target.closest('[data-gx-ec-event]') : null;
    if (ec && ecActivationCounts(ec, target)) {
      trackEcommerce(ec.getAttribute('data-gx-ec-event'), ecPayload(ec));
    }

    // select_promotion: click on a data-gx-promotion element.
    var promo = target.closest ? target.closest('[data-gx-promotion]') : null;
    if (promo) {
      trackEcommerce('select_promotion', promotionData(promo));
    }

    // navigation from a <summary data-link>: the header's top level and its
    // hover dropdowns navigate from the summary, not from a link, so this is
    // resolved before the a[href] guard below.
    var summary = target.closest ? target.closest('summary') : null;
    var summaryBar = summary ? summary.closest('[data-gx-nav]') : null;
    if (summaryBar) {
      var summaryUrl = summaryNavigatesTo(summary);
      if (summaryUrl && !(tracked && isRelated(summary, tracked))) {
        trackNavigation(summaryBar, summary, summaryUrl);
      }
      // The summary swallows the click, so a link nested inside it (the
      // tabindex="-1" anchor in desktop-menu.liquid) never navigates on its
      // own - stop here whether or not the summary itself navigated.
      return;
    }

    var link = target.closest ? target.closest('a[href]') : null;
    if (!link) return;
    // Skip links already handled by the data-gx-event branch above.
    if (tracked && (link === tracked || tracked.contains(link) || link.contains(tracked))) {
      return;
    }

    var href = link.getAttribute('href') || '';

    // logout: link pointing at the Shopify logout endpoint.
    if (/\/account\/logout/i.test(href)) {
      track('logout', { login_status: 'logged_out' });
      return;
    }

    // navigation: link inside a data-gx-nav container. navigation_bar is the
    // container's value; navigation_element is the link's data-gx-nav-element
    // or its text. Placeholder hrefs are toggles, not navigations - they are
    // dropped rather than falling through to the mailto/tel/download branch.
    var navContainer = link.closest('[data-gx-nav]');
    if (navContainer) {
      if (!isPlaceholderHref(href)) trackNavigation(navContainer, link, href);
      return;
    }

    // contact_click for mailto:/tel: links, file_download for document links.
    if (/^mailto:/i.test(href)) {
      track('contact_click', { link_type: 'mailto', link_url: href });
    } else if (/^tel:/i.test(href)) {
      track('contact_click', { link_type: 'tel', link_url: href });
    } else if (DOWNLOAD_RE.test(href)) {
      track('file_download', {
        file_name: href,
        file_extension: fileExtension(href),
        source_page_type: currentPageType()
      });
    }
  }

  // The element whose pointer activation was already tracked; the click that
  // follows consumes the token instead of tracking again. Time-independent, so
  // a slow press or long tap cannot slip through a time window.
  var gxPendingActivation = null;
  // Guards against a double click producing two events for one navigation.
  var gxLastActivation = { el: null, at: 0 };
  var GX_REPEAT_MS = 300;

  // True when el (or an ancestor/descendant of it) is the pending activation.
  function isRelated(el, other) {
    if (!el || !other) return false;
    return el === other || (other.contains && other.contains(el)) || (el.contains && el.contains(other));
  }

  function activate(target, viaPointer) {
    if (!target || target.nodeType !== 1) return;
    if (viaPointer) {
      // Claim the click that closes this pointer sequence before anything else:
      // it must be consumed even when the repeat guard drops the event itself,
      // otherwise the click fallback would re-fire what was just suppressed.
      gxPendingActivation = target;
      // Same element again within 300ms (double click): one activation, one event.
      // A negative delta (clock stepped backwards) must not suppress anything.
      var sinceLast = Date.now() - gxLastActivation.at;
      if (isRelated(target, gxLastActivation.el) && sinceLast >= 0 && sinceLast < GX_REPEAT_MS) {
        return;
      }
      gxLastActivation = { el: target, at: Date.now() };
    }
    rememberListAttr(target);
    handleActivation(target);
  }

  // ---- Drag guard (draggable carousels) ----------------------------------
  // Inside a Swiper that accepts drags a press is not yet a selection: it may
  // turn into a swipe, and Swiper then swallows the click so nothing is opened.
  // Two things follow from that:
  //   - the mouse cannot activate on pointerdown there, it has to wait for
  //     pointerup like touch does (still before the click, so no navigation is
  //     raced), and
  //   - the press only counts when Swiper still allows the click.
  // `allowClick` is Swiper's own verdict: false from the first pointermove that
  // starts a drag, reset asynchronously after touchEnd, so in this
  // document-capture handler it always describes the press that just ended.
  // That beats a pixel threshold of our own, which would either drop jittery
  // clicks or keep firing on drags that snapped back to their start. Outside a
  // draggable slider nothing about the activation timing changes.
  var gxMousePress = null;

  // The Swiper around el, but only while it actually accepts drags.
  // allowTouchMove is read live - media-gallery.js toggles it per breakpoint.
  function draggableSlider(el) {
    var root = el && el.closest ? el.closest('.swiper') : null;
    var swiper = root && root.swiper;
    return swiper && swiper.allowTouchMove !== false ? swiper : null;
  }

  // True when the press that just ended was a drag inside such a slider.
  function wasDragged(el) {
    var swiper = draggableSlider(el);
    return !!swiper && swiper.allowClick === false;
  }

  document.addEventListener(
    'pointerdown',
    function (ev) {
      // Touch and pen activate on pointerup: a press that turns into a scroll
      // must not count as a selection. Mouse gains the whole press duration,
      // except inside a draggable slider (see "Drag guard" above).
      if (ev.pointerType === 'touch' || ev.pointerType === 'pen') return;
      // Cleared unconditionally: a press released outside the window never gets
      // a pointerup, and must not be activated by the next one.
      gxMousePress = null;
      // Left and middle button only - a right click opens the context menu.
      if (ev.button !== 0 && ev.button !== 1) return;
      if (draggableSlider(ev.target)) {
        gxMousePress = ev.target;
        return;
      }
      activate(ev.target, true);
    },
    true
  );

  document.addEventListener(
    'pointerup',
    function (ev) {
      var pressed = gxMousePress;
      gxMousePress = null;
      var isTouch = ev.pointerType === 'touch' || ev.pointerType === 'pen';
      // A mouse press with nothing pending was already tracked on pointerdown.
      if (!isTouch && !pressed) return;
      // The press target, not the release target: a drag can end on a neighbour.
      var target = pressed || ev.target;
      if (wasDragged(target)) return;
      activate(target, true);
    },
    true
  );

  // True for elements that Space activates. On a link Space only scrolls, so
  // counting it would invent selections for keyboard users.
  function spaceActivates(el) {
    var tag = (el.tagName || '').toLowerCase();
    if (tag === 'button' || tag === 'summary') return true;
    if (el.getAttribute && el.getAttribute('role') === 'button') return true;
    return tag === 'input' && /^(submit|button|checkbox|radio)$/i.test(el.type || '');
  }

  document.addEventListener(
    'keydown',
    function (ev) {
      if (ev.repeat) return;
      var isSpace = ev.key === ' ' || ev.key === 'Spacebar';
      if (ev.key !== 'Enter' && !isSpace) return;
      if (isSpace && !spaceActivates(ev.target)) return;
      activate(ev.target, true);
    },
    true
  );

  document.addEventListener(
    'click',
    function (ev) {
      // Fallback only: consume the token when this click was already tracked
      // on pointerdown/pointerup/keydown.
      if (isRelated(ev.target, gxPendingActivation)) {
        gxPendingActivation = null;
        return;
      }
      // A drag inside a slider still produces a click when press and release
      // share a target. Swiper cancels that click from its own bubble-phase
      // handler, which runs after this one, so the drag has to be re-checked
      // here or the fallback would undo what pointerup deliberately skipped.
      if (wasDragged(ev.target)) return;
      activate(ev.target, false);
    },
    true
  );

  // ---- First-focus tracking (login_begin, sign_up_begin, form_interaction)
  // Fires the data-gx-focus-event once per container, when a field inside it
  // first receives focus.
  var focusedHosts = new WeakSet();
  document.addEventListener(
    'focusin',
    function (ev) {
      var host = ev.target.closest ? ev.target.closest('[data-gx-focus-event]') : null;
      if (!host || focusedHosts.has(host)) return;
      focusedHosts.add(host);
      track(host.getAttribute('data-gx-focus-event'), collectParams(host));
    },
    true
  );

  // ---- Submit tracking (account_modification, generate_lead, …) ---------
  // Fires the data-gx-submit-event when a form inside the host is submitted.
  document.addEventListener(
    'submit',
    function (ev) {
      var host = ev.target.closest ? ev.target.closest('[data-gx-submit-event]') : null;
      if (!host) return;
      track(host.getAttribute('data-gx-submit-event'), collectParams(host));
    },
    true
  );

  // ---- Paperform (cross-origin iframe) form tracking --------------------
  // Maps the signals the paperform.co embed sends to the parent window onto GA4
  // events via the data-gx-* hooks on <paperform-handler> (see brita-paperform.liquid):
  //   PaperformSubmission (DOM CustomEvent) -> data-gx-submit-event (newsletter_soi / form_submit)
  //   StartedSubmission   (postMessage)     -> data-gx-interaction-event (b2b_contact_<unit>_start)
  //   PaperformPageChange (DOM CustomEvent) -> data-gx-step-event (b2b_contact_<unit>_step_<n>)

  // Returns the data-gx-* host for a PaperformSubmission DOM event.
  function gxPfHost(ev) {
    var el = ev.target;
    return el && el.closest
      ? el.closest('[data-gx-submit-event],[data-gx-interaction-event]')
      : null;
  }

  document.addEventListener(
    'PaperformSubmission',
    function (ev) {
      var host = gxPfHost(ev);
      if (!host) return;
      var name = host.getAttribute('data-gx-submit-event');
      if (!name) return;
      var params = collectParams(host);
      if (ev.detail && ev.detail.form_id && !params.form_id) {
        params.form_id = ev.detail.form_id;
      }
      track(name, params);
    },
    true
  );

  // Step events: PaperformPageChange fires on every page change, forward and
  // backward, with detail { form_id, currentPage, totalPages } (currentPage is
  // the page just reached, 1-based). Every completed step is its own event:
  // reaching page 2 completes step 1 -> <base>_1, reaching page 3 -> <base>_2.
  // The last step is not reported here, its completion is the submit event.
  // Each step is reported once per form so going back and forth cannot inflate
  // the funnel.
  var gxPfSteps = new WeakMap();

  document.addEventListener(
    'PaperformPageChange',
    function (ev) {
      var el = ev.target;
      var host = el && el.closest ? el.closest('[data-gx-step-event]') : null;
      if (!host) return;
      var base = host.getAttribute('data-gx-step-event');
      var detail = ev.detail || {};
      var step = Number(detail.currentPage) - 1;
      // Going back to page 1 completes no step.
      if (!base || !isFinite(step) || step < 1) return;
      var seen = gxPfSteps.get(host) || {};
      if (seen[step]) return;
      seen[step] = true;
      gxPfSteps.set(host, seen);

      var params = collectParams(host);
      if (detail.form_id && !params.form_id) params.form_id = detail.form_id;
      params.step_number = step;
      if (isFinite(Number(detail.totalPages))) params.total_steps = Number(detail.totalPages);
      track(base + '_' + step, params);
    },
    true
  );

  // form_interaction: StartedSubmission arrives as a postMessage with no DOM
  // target, so the host is resolved by matching the form slug to the embed
  // div's data-paperform-id. Fires the data-gx-interaction-event once per form.
  //   e.data === 'paperform:{"details":{"form_id","eventName","payload":{"form"}},
  //               "action":"analytics","id":<slug>}'
  var gxPfInteracted = new WeakSet();

  // Parses a Paperform postMessage payload (object or 'paperform:'-prefixed JSON).
  function gxParsePfMessage(data) {
    if (data && typeof data === 'object') return data;
    if (typeof data === 'string' && data.indexOf('paperform:') === 0) {
      try {
        return JSON.parse(data.slice('paperform:'.length));
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  // Returns the data-gx-* host for a Paperform form slug.
  function gxPfHostBySlug(slug) {
    if (!slug) return null;
    var div = document.querySelector('[data-paperform-id="' + slug + '"]');
    return div && div.closest
      ? div.closest('[data-gx-submit-event],[data-gx-interaction-event]')
      : null;
  }

  // True when the message origin is paperform.co (or a subdomain).
  function gxIsPaperformOrigin(origin) {
    try {
      var h = new URL(origin).hostname;
      return h === 'paperform.co' || /\.paperform\.co$/.test(h);
    } catch (e) {
      return false;
    }
  }

  window.addEventListener('message', function (ev) {
    if (!gxIsPaperformOrigin(ev.origin)) return;
    var msg = gxParsePfMessage(ev.data);
    if (!msg || msg.action !== 'analytics') return;
    var details = msg.details || {};
    if (details.eventName !== 'StartedSubmission') return;

    var slug = (details.payload && details.payload.form) || msg.id;
    var host = gxPfHostBySlug(slug);
    if (!host) return;
    var name = host.getAttribute('data-gx-interaction-event');
    if (!name || gxPfInteracted.has(host)) return;
    gxPfInteracted.add(host);

    var params = collectParams(host);
    if (details.form_id && !params.form_id) params.form_id = details.form_id;
    track(name, params);
  });

  // ---- <details> open tracking (e.g. faq_expand) ------------------------
  // Fires the data-gx-toggle-event when a <details> inside the host is opened.
  document.addEventListener(
    'toggle',
    function (ev) {
      var details = ev.target;
      if (!details || details.nodeName !== 'DETAILS' || !details.open) return;
      var host = details.closest('[data-gx-toggle-event]');
      if (!host) return;
      track(host.getAttribute('data-gx-toggle-event'), collectParams(host));
    },
    true
  );

  // ---- swipe_content on CSS scroll-sliders -------------------------------
  // Swiper-driven carousels fire swipe_content from the Carousel hook in
  // theme.js. Sections that only use the CSS scroll-snap slider
  // (`.swipe-mobile` / `.swipe-tablet`, e.g. multicolumn or buttons-with-icon
  // without `enable_slider`) have no Swiper instance, so their gesture is
  // tracked here. Fires once per settled gesture, only on horizontal movement
  // and only when a user interaction preceded it - programmatic scrolls
  // (scrollIntoView, anchor jumps) stay silent, mirroring the Swiper hook.
  var GX_SWIPE_SETTLE = 150;
  var GX_SWIPE_INTENT_MS = 2000;
  var GX_SWIPE_MIN_PX = 8;
  var gxSwipeTimers = new WeakMap();
  var gxSwipeLast = new WeakMap();
  var gxSwipeIntent = null;

  // The attribute may sit on the scroll container itself, on an ancestor, or
  // on the inner track (multicolumn puts it on `.swipe-mobile__inner`).
  function gxSwipeHost(el) {
    if (!el || el.nodeType !== 1 || typeof el.closest !== 'function') return null;
    return el.closest('[data-gx-swipe-content]') || el.querySelector('[data-gx-swipe-content]');
  }

  function gxSwipeUserDriven(el) {
    if (!gxSwipeIntent) return false;
    if (nowMs() - gxSwipeIntent.time > GX_SWIPE_INTENT_MS) return false;
    var origin = gxSwipeIntent.target;
    return !!origin && (el === origin || el.contains(origin));
  }

  function gxSwipeSettled(el) {
    gxSwipeTimers.delete(el);
    var previous = gxSwipeLast.get(el);
    var position = el.scrollLeft;
    gxSwipeLast.set(el, position);
    if (typeof previous === 'number' && Math.abs(position - previous) < GX_SWIPE_MIN_PX) return;
    if (!gxSwipeUserDriven(el)) return;
    var host = gxSwipeHost(el);
    if (!host) return;
    // Sections that enable both the Swiper and the CSS slider (e.g.
    // buttons-with-icon with `enable_slider` + `swipe_on_mobile`) already
    // report through the Carousel hook - never count one gesture twice.
    if (host.classList.contains('swiper-initialized') || host.querySelector('.swiper-initialized')) return;
    track('swipe_content', {
      content_type: host.getAttribute('data-gx-swipe-content') || 'slider'
    });
  }

  function nowMs() {
    return Date.now ? Date.now() : new Date().getTime();
  }

  ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach(function (type) {
    document.addEventListener(
      type,
      function (ev) {
        gxSwipeIntent = { target: ev.target, time: nowMs() };
      },
      { capture: true, passive: true }
    );
  });

  // `scroll` does not bubble, so it is captured from the document to cover
  // sections rendered later (section reload in the theme editor, cart drawer).
  var gxSwipeSupportsEnd = 'onscrollend' in window;
  document.addEventListener(
    'scroll',
    function (ev) {
      var el = ev.target;
      if (!el || el.nodeType !== 1) return;
      if (el.scrollWidth - el.clientWidth < GX_SWIPE_MIN_PX) return;
      if (!gxSwipeHost(el)) return;
      // Keep the intent alive while the gesture is still producing scroll, so
      // long drags and touch momentum still count as user-driven when they
      // finally settle.
      if (gxSwipeUserDriven(el)) gxSwipeIntent.time = nowMs();
      if (gxSwipeSupportsEnd) return;
      var timer = gxSwipeTimers.get(el);
      if (timer) clearTimeout(timer);
      gxSwipeTimers.set(
        el,
        setTimeout(function () {
          gxSwipeSettled(el);
        }, GX_SWIPE_SETTLE)
      );
    },
    true
  );

  if (gxSwipeSupportsEnd) {
    document.addEventListener(
      'scrollend',
      function (ev) {
        var el = ev.target;
        if (!el || el.nodeType !== 1) return;
        if (el.scrollWidth - el.clientWidth < GX_SWIPE_MIN_PX) return;
        gxSwipeSettled(el);
      },
      true
    );
  }

  // ---- view_promotion: fires once when a data-gx-promotion element becomes
  // visible.
  //
  // Geometry alone is not enough. The slideshow runs Swiper's fade effect, so
  // every slide sits at the same position and only differs in opacity - an
  // IntersectionObserver sees all of them as fully visible and would fire the
  // whole set on load. Every candidate therefore passes an additional
  // visibility check, and elements that intersect while still invisible stay
  // observed (`gxPendingPromos`) until they actually show up.
  var gxPendingPromos = [];
  var gxPromoObserver = null;

  // True when the element is not just in the viewport but actually rendered -
  // catches opacity/visibility, which the IntersectionObserver ignores.
  function promotionIsVisible(el) {
    // Swiper only clones slides in older loop implementations; a clone would
    // count the same promotion twice.
    if (el.closest && el.closest('.swiper-slide-duplicate')) return false;
    if (typeof el.checkVisibility === 'function') {
      return el.checkVisibility({ opacityProperty: true, visibilityProperty: true });
    }
    // Fallback for browsers without checkVisibility: trust the slider state.
    if (el.closest && el.closest('[aria-hidden="true"]')) return false;
    var slide = el.closest ? el.closest('.swiper-slide') : null;
    if (
      slide &&
      !slide.classList.contains('swiper-slide-active') &&
      !slide.classList.contains('swiper-slide-visible')
    ) {
      return false;
    }
    return true;
  }

  function firePromotionView(el) {
    if (el._gxViewed) return;
    el._gxViewed = true;
    if (gxPromoObserver) gxPromoObserver.unobserve(el);
    trackEcommerce('view_promotion', promotionData(el));
  }

  // Half of the element is in the viewport - the same bar the
  // IntersectionObserver applies, re-checked because a pending promotion may
  // have been scrolled away since it was queued.
  function promotionInViewport(el) {
    var rect = el.getBoundingClientRect();
    if (!rect.height) return false;
    var visible = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
    return visible / rect.height >= 0.5;
  }

  // Re-checks the promotions that are in the viewport but were still hidden.
  // Called from the carousel hook in theme.js after a slide change: with the
  // fade effect the geometry never changes, so the IntersectionObserver alone
  // would never fire a second time. Runs synchronously - the hook fires once
  // per slide change, and by then the slide classes and opacities are final.
  function recheckPendingPromotions() {
    if (!gxPendingPromos.length) return;
    gxPendingPromos = gxPendingPromos.filter(function (el) {
      if (el._gxViewed) return false;
      if (!promotionIsVisible(el) || !promotionInViewport(el)) return true;
      firePromotionView(el);
      return false;
    });
  }

  function initPromotionViews() {
    if (!('IntersectionObserver' in window)) return;
    var promos = document.querySelectorAll('[data-gx-promotion]');
    if (!promos.length) return;
    gxPromoObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          if (el._gxViewed) return;
          if (!promotionIsVisible(el)) {
            if (gxPendingPromos.indexOf(el) === -1) gxPendingPromos.push(el);
            return;
          }
          firePromotionView(el);
        });
      },
      { threshold: 0.5 }
    );
    promos.forEach(function (el) {
      gxPromoObserver.observe(el);
    });
  }

  // Runs on `load`, not `DOMContentLoaded`: sliders must have initialised, or
  // their slides are briefly laid out side by side and all count as visible.
  if (document.readyState === 'complete') {
    initPromotionViews();
  } else {
    window.addEventListener('load', initPromotionViews);
  }

  // ---- Ecommerce events -------------------------------------------------
  //   view_item / view_cart (cart page) -> BritaGxConfig.ecommerce (Liquid)
  //   view_item_list                    -> aggregated from select_item card payloads
  //                                        (re-scanned when cards are injected)
  //   add_to_cart / remove_from_cart    -> diffed from the `cart:updated` bus
  //   view_cart (drawer)                -> on cart drawer open
  // Items carry the GA4 core fields (item_id/name/brand/variant/category,
  // price, quantity) from standard product/cart data.
  //
  // The list a line was added from is also persisted into the cart attribute
  // `_gx_list`, which the Web Pixel reads to enrich the checkout funnel - see
  // "Durable line attribution" below.
  var GxEc = { snapshot: null, currency: '' };

  // ---- List attribution (card -> add_to_cart) ---------------------------
  // add_to_cart is diffed from /cart.js data, which knows nothing about where
  // the product was engaged. Any activation inside a card with
  // data-gx-list-attr therefore records its list, keyed by product id, and
  // fireCartDelta looks it up. sessionStorage carries the attribution across the
  // navigation to the PDP (card -> PDP -> add to cart); the parsed store is kept
  // in memory so a lookup never re-parses.
  //
  // Product level, not variant level: a variant switch on the PDP keeps the list
  // it was reached from, and one key per product means a later contact with a
  // different list always overwrites the earlier one (last touch wins).
  //
  // Deliberately narrow, unlike the removed BritaGxItems registry: two strings
  // per product, no item enrichment, size-capped. No TTL: sessionStorage already
  // dies with the tab, and expiring entries could only ever lose attribution.
  var GX_ATTR_KEY = 'brita_gx_list_attr';
  var GX_ATTR_MAX = 50; // newest entries win
  var gxAttr = null;

  // Loads (once) the attribution store. recorded_at is required (attrPersist
  // sorts on it); the key prefix check drops the variant: entries a session
  // started on the previous, variant-keyed version may still hold.
  function attrStore() {
    if (gxAttr) return gxAttr;
    gxAttr = {};
    try {
      var raw = window.sessionStorage.getItem(GX_ATTR_KEY);
      var parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === 'object') {
        Object.keys(parsed).forEach(function (key) {
          var entry = parsed[key];
          if (entry && entry.recorded_at && key.indexOf('product:') === 0) {
            gxAttr[key] = entry;
          }
        });
      }
    } catch (e) {
      /* private mode or malformed value: in-memory only */
    }
    return gxAttr;
  }

  // Persists the store, keeping only the GX_ATTR_MAX newest entries.
  function attrPersist() {
    var store = attrStore();
    var keys = Object.keys(store);
    if (keys.length > GX_ATTR_MAX) {
      keys
        .sort(function (a, b) {
          return store[b].recorded_at - store[a].recorded_at;
        })
        .slice(GX_ATTR_MAX)
        .forEach(function (key) {
          delete store[key];
        });
    }
    try {
      window.sessionStorage.setItem(GX_ATTR_KEY, JSON.stringify(store));
    } catch (e) {
      /* quota or private mode: in-memory only */
    }
  }

  // Records the list of the card the activation happened in (no-op elsewhere).
  function rememberListAttr(target) {
    var host = target.closest ? target.closest('[data-gx-list-attr]') : null;
    if (!host) return;
    var data = host._gxListAttr;
    if (!data) {
      try {
        data = JSON.parse(host.getAttribute('data-gx-list-attr') || '{}');
      } catch (e) {
        data = {};
      }
      host._gxListAttr = data;
    }
    if (!data.list_id && !data.list_name) return;
    if (!data.product_id) return;
    var store = attrStore();
    store['product:' + data.product_id] = {
      list_id: data.list_id == null ? '' : String(data.list_id),
      list_name: data.list_name || '',
      recorded_at: Date.now()
    };
    attrPersist();
  }

  // Returns the remembered list for a cart line, or null. /cart.js and
  // `cart:updated` lines both carry product_id.
  function lookupListAttr(line) {
    if (!line.product_id) return null;
    return attrStore()['product:' + line.product_id] || null;
  }

  // ---- Durable line attribution (add_to_cart -> checkout pixel) ----------
  // The list a line was added from has to survive the whole cart lifetime, so
  // the Web Pixel can enrich begin_checkout / add_shipping_info /
  // add_payment_info / purchase. Line item properties would be the obvious
  // carrier, but Shopify splits a line whenever the same variant is added with
  // differing properties ("if the same items have differing prices, properties,
  // or selling plans, then they'll be split into their own line items") - one
  // missed property write means a duplicate line in the cart. A cart attribute
  // sits outside the line merge key and therefore cannot split anything:
  //
  //   attributes._gx_list = {
  //     "<variant>[:<plan>]": {list_id: …, list_name: …, recorded_at: …}
  //   }
  //
  // The entry keys are spelled out, not abbreviated: this value is read by hand
  // in /cart.js, in the admin and during QA, and it has to be self-explanatory
  // there. Same field names as the Ebene 1 store above.
  //
  // The server is the source of truth: every /cart.js response carries
  // `attributes`, so each cart update re-syncs the map and parallel tabs merge
  // instead of clobbering each other. localStorage keeps a mirror, used only as
  // the pixel's fallback when a checkout event carries no attributes.
  //
  // Write rule: the first ATC per key wins - a later card contact must not
  // rewrite a line that is already in the cart (that is what makes Ebene 1's
  // last-touch store safe to build on). Delete rule: keys without a matching
  // cart line are pruned, so removing and re-adding a product picks up the list
  // it was re-added from.
  var GX_LINE_ATTR = '_gx_list'; // cart attribute key
  var GX_LINE_MIRROR = 'brita_gx_line_attr'; // localStorage mirror (pixel fallback)
  var GX_LINE_MAX = 30; // guard only: prune keeps the map at cart size
  var GX_LINE_MAX_CHARS = 4000; // stay well inside the cart attribute limit
  var GX_LINE_DEBOUNCE = 150; // ms; keepalive covers a navigation mid-write
  var gxLine = null; // key -> {list_id, list_name, recorded_at}
  var gxLineSynced = ''; // attribute value last seen on the server
  var gxLineWritten = null; // attribute value of the last successful write
  var gxLineTimer = null;
  var gxLineWriting = false;

  // Kill switch: BritaGxConfig.line_attr = false disables the whole block
  // (no cart attribute, no mirror, no extra request) without touching the pixel.
  function lineAttrEnabled() {
    return !(window.BritaGxConfig && window.BritaGxConfig.line_attr === false);
  }

  // Selling plan id of a cart line, '' when there is none. /cart.js nests it
  // under selling_plan_allocation, `cart:updated` lines carry it top level.
  function linePlanId(line) {
    var plan =
      (line.selling_plan_allocation && line.selling_plan_allocation.selling_plan) ||
      line.selling_plan ||
      null;
    return plan && plan.id ? String(plan.id) : '';
  }

  // Map key for a cart line: the variant, plus the selling plan when there is
  // one - the same variant as subscription and as one-off are two lines and must
  // carry their own attribution. Both parts are joinable in the pixel
  // (lineItem.variant.id / sellingPlanAllocation.sellingPlan.id); the cart line
  // key is not, it never reaches the checkout events.
  function lineAttrKey(line) {
    var variant = line.variant_id || line.id;
    if (!variant) return '';
    var plan = linePlanId(line);
    return plan ? String(variant) + ':' + plan : String(variant);
  }

  // Parses a stored map, keeping only well-formed entries.
  function lineAttrParse(raw) {
    var out = {};
    var parsed = null;
    try {
      parsed = raw ? JSON.parse(raw) : null;
    } catch (e) {
      return out;
    }
    if (!parsed || typeof parsed !== 'object') return out;
    Object.keys(parsed).forEach(function (key) {
      var entry = parsed[key];
      if (!entry || typeof entry !== 'object') return;
      if (!entry.list_id && !entry.list_name) return;
      out[key] = {
        list_id: entry.list_id ? String(entry.list_id) : '',
        list_name: entry.list_name || '',
        recorded_at: Number(entry.recorded_at) || 0
      };
    });
    return out;
  }

  // The in-memory map, seeded once from the localStorage mirror. The cart
  // attribute is merged in by lineAttrAdopt as soon as the first cart arrives.
  function lineStore() {
    if (gxLine) return gxLine;
    var raw = '';
    try {
      raw = window.localStorage.getItem(GX_LINE_MIRROR) || '';
    } catch (e) {
      /* private mode: in-memory only */
    }
    gxLine = lineAttrParse(raw);
    return gxLine;
  }

  // Returns the raw _gx_list value of a cart, or ''.
  function cartAttrValue(cart) {
    var attrs = (cart && cart.attributes) || {};
    var value = attrs[GX_LINE_ATTR];
    return typeof value === 'string' ? value : '';
  }

  // Merges the server-side map into memory. Union, never replace: a key this tab
  // just recorded is not on the server yet, and a key another tab recorded is
  // not in this tab's memory. Stale keys cannot accumulate because lineAttrPrune
  // drops everything without a cart line.
  function lineAttrAdopt(cart) {
    var raw = cartAttrValue(cart);
    // Trust our own last write when a cart payload does not echo the attribute
    // back. Underscore-prefixed attributes are hidden in several Shopify
    // surfaces; if one of them ever hides it here, "no attribute" would look
    // like "never written" and every cart update would re-post the same map.
    gxLineSynced = raw || (gxLineWritten == null ? '' : gxLineWritten);
    var server = lineAttrParse(raw);
    var store = lineStore();
    Object.keys(server).forEach(function (key) {
      if (!store[key]) store[key] = server[key];
    });
  }

  // Records the list of the ATC that created a line. First write per key wins.
  function lineAttrRecord(line, attr) {
    if (!lineAttrEnabled()) return;
    if (!attr || (!attr.list_id && !attr.list_name)) return;
    var key = lineAttrKey(line);
    if (!key) return;
    var store = lineStore();
    if (store[key]) return;
    store[key] = {
      list_id: attr.list_id ? String(attr.list_id) : '',
      list_name: attr.list_name || '',
      recorded_at: Date.now()
    };
  }

  // Drops keys without a matching cart line. This is what keeps the map fresh:
  // a removed line loses its attribution, and it also cleans up after a
  // completed order (empty cart -> empty map).
  function lineAttrPrune(cart) {
    var live = {};
    ((cart && cart.items) || []).forEach(function (line) {
      var key = lineAttrKey(line);
      if (key) live[key] = true;
    });
    var store = lineStore();
    Object.keys(store).forEach(function (key) {
      if (!live[key]) delete store[key];
    });
  }

  // Caps the map by entry count and serialized size, oldest first, so the cart
  // attribute can never grow unbounded. Prune normally keeps it at cart size.
  function lineAttrTrim() {
    var store = lineStore();
    var keys = Object.keys(store).sort(function (a, b) {
      return store[b].recorded_at - store[a].recorded_at;
    });
    keys.slice(GX_LINE_MAX).forEach(function (key) {
      delete store[key];
    });
    keys = keys.slice(0, GX_LINE_MAX);
    while (keys.length && JSON.stringify(store).length > GX_LINE_MAX_CHARS) {
      delete store[keys.pop()];
    }
  }

  // Mirrors the map to localStorage (the pixel's fallback) - best effort.
  function lineAttrMirror(json) {
    try {
      if (json === '{}') window.localStorage.removeItem(GX_LINE_MIRROR);
      else window.localStorage.setItem(GX_LINE_MIRROR, json);
    } catch (e) {
      /* quota or private mode: attribute only */
    }
  }

  // Writes the map to the cart attribute when it differs from the value the
  // server is known to hold (an empty map removes the attribute). A failed write
  // leaves gxLineSynced untouched, so the next cart update retries by itself.
  // keepalive so a write started right before a navigation still arrives.
  function lineAttrFlush() {
    lineAttrTrim();
    var json = JSON.stringify(lineStore());
    lineAttrMirror(json);
    var value = json === '{}' ? '' : json;
    if (value === gxLineSynced) return;
    if (gxLineWriting) {
      lineAttrSchedule(); // retry once the in-flight write has landed
      return;
    }
    gxLineWriting = true;
    var body = { attributes: {} };
    body.attributes[GX_LINE_ATTR] = value;
    try {
      var root =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
        keepalive: true
      })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          gxLineWriting = false;
          gxLineWritten = value;
          if (DEBUG && window.console && console.log) {
            console.log('[BritaGx] list map written', value, cart && cart.attributes);
          }
          // The response is the fresh cart: adopt a parallel tab's keys, prune
          // dead ones, and only write again if that changed something.
          lineAttrAdopt(cart);
          lineAttrPrune(cart);
          lineAttrSchedule();
        })
        .catch(function () {
          gxLineWriting = false; // gxLineSynced unchanged -> next cart update retries
        });
    } catch (e) {
      gxLineWriting = false;
    }
  }

  function lineAttrSchedule() {
    if (!lineAttrEnabled() || gxLineTimer) return;
    gxLineTimer = setTimeout(function () {
      gxLineTimer = null;
      lineAttrFlush();
    }, GX_LINE_DEBOUNCE);
  }

  // Converts a price in cents to a unit amount.
  function centsToUnit(cents) {
    return Math.round(Number(cents) || 0) / 100;
  }

  // Maps a Shopify /cart.js line item (or `cart:updated` line) to a GA4 item.
  function itemFromCartLine(line) {
    // Selling plan lives under selling_plan_allocation on /cart.js lines, or as
    // a top-level selling_plan on cart:updated lines.
    var sellingPlan =
      (line.selling_plan_allocation && line.selling_plan_allocation.selling_plan) ||
      line.selling_plan ||
      null;
    return {
      item_id: line.sku || String(line.variant_id || line.id || ''),
      item_name: line.product_title || line.title || '',
      item_variant: line.variant_title || '',
      price: centsToUnit(line.final_price != null ? line.final_price : line.price),
      currency: GxEc.currency || '',
      quantity: line.quantity || 0,
      item_subscription: sellingPlan ? 'yes' : 'no',
      item_subscription_id: sellingPlan && sellingPlan.id ? String(sellingPlan.id) : ''
    };
  }

  // Indexes a cart by line key into { quantity, line }, and records the currency.
  function indexCart(cart) {
    GxEc.currency = (cart && cart.currency) || GxEc.currency || '';
    var map = {};
    ((cart && cart.items) || []).forEach(function (line) {
      map[line.key || line.id] = { quantity: line.quantity || 0, line: line };
    });
    return map;
  }

  // Pushes an add_to_cart / remove_from_cart for a single line and quantity delta.
  function fireCartDelta(name, line, deltaQty) {
    var qty = Math.abs(deltaQty);
    if (!qty) return;
    var item = itemFromCartLine(line);
    item.quantity = qty;
    // Item-scoped list attribution, only for add_to_cart: where a line was
    // removed is not a list. Unknown attribution stays absent rather than ''.
    if (name === 'add_to_cart') {
      var attr = lookupListAttr(line);
      if (attr && attr.list_id) item.item_list_id = attr.list_id;
      if (attr && attr.list_name) item.item_list_name = attr.list_name;
      // Persist it for the checkout pixel. This is the only place that knows
      // "this ATC created/grew this line", so the durable map is written here and
      // nowhere else - a quantity increase finds its key already set.
      lineAttrRecord(line, attr);
    }
    trackEcommerce(name, {
      currency: item.currency,
      value: item.price * qty,
      items: [item]
    });
  }

  // Diffs the incoming cart against the last snapshot: quantity increases ->
  // add_to_cart, decreases/removals -> remove_from_cart.
  function diffCart(cart) {
    var next = indexCart(cart);
    if (!GxEc.snapshot) {
      GxEc.snapshot = next; // first cart seen: establish baseline, don't emit
      return;
    }
    var prev = GxEc.snapshot;
    var key;
    for (key in next) {
      if (!Object.prototype.hasOwnProperty.call(next, key)) continue;
      var before = prev[key] ? prev[key].quantity : 0;
      if (next[key].quantity > before) {
        fireCartDelta('add_to_cart', next[key].line, next[key].quantity - before);
      }
    }
    for (key in prev) {
      if (!Object.prototype.hasOwnProperty.call(prev, key)) continue;
      var after = next[key] ? next[key].quantity : 0;
      if (after < prev[key].quantity) {
        fireCartDelta('remove_from_cart', prev[key].line, prev[key].quantity - after);
      }
    }
    GxEc.snapshot = next;
  }

  // Every cart the tracking sees goes through here. Order matters: the server
  // map is adopted BEFORE the diff, so a line another tab already attributed
  // keeps its list (first-write-wins is decided against server state, not
  // against this tab's memory only). The diff then records new lines, and the
  // prune afterwards drops keys whose line is gone.
  function handleCart(cart) {
    var attrOn = lineAttrEnabled();
    if (attrOn) lineAttrAdopt(cart);
    diffCart(cart);
    if (attrOn) {
      lineAttrPrune(cart);
      lineAttrSchedule();
    }
  }

  // Fetches /cart.js and passes the parsed cart to cb.
  function fetchCart(cb) {
    try {
      var root =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          cb(cart);
        })
        .catch(function () {
          /* ignore */
        });
    } catch (e) {
      /* ignore */
    }
  }

  // Pushes view_cart for the given cart.
  function fireViewCart(cart) {
    if (!cart) return;
    GxEc.currency = cart.currency || GxEc.currency || '';
    trackEcommerce('view_cart', {
      currency: GxEc.currency,
      value: centsToUnit(cart.total_price),
      items: (cart.items || []).map(itemFromCartLine)
    });
  }

  var GX_CARD_SEL = '[data-gx-ec-event="select_item"]';

  // Last emitted item signature per list key (guards against re-firing the same
  // list with an unchanged item set).
  var gxFiredLists = {};

  // ---- Collection layout: grid vs. quick order list ----------------------
  // The collection grid renders either as a product grid or as a quick order
  // list; the layout-switcher writes the mode to #ProductsList[data-layout]
  // (assets/facets.js). Both views show the same products, so the ecommerce
  // payload is identical - only the event name follows the view. Lists outside
  // the collection grid (recommendations, predictive search, mega menu) are
  // always view_item_list.
  var GX_VIEW_EVENT_BY_LAYOUT = { grid: 'view_item_list', list: 'quick_order_list_view' };

  function gxCollectionGrid() {
    return document.getElementById('ProductsList');
  }

  // Event name for a list, from the layout of the collection grid that contains
  // `el`. #ProductsList carries no data-layout until the switcher sets one, so
  // an absent value means the Liquid default: grid.
  function gxListEventName(el) {
    var grid = gxCollectionGrid();
    if (!grid || !el || !(grid === el || grid.contains(el))) {
      return GX_VIEW_EVENT_BY_LAYOUT.grid;
    }
    return GX_VIEW_EVENT_BY_LAYOUT[grid.dataset.layout] || GX_VIEW_EVENT_BY_LAYOUT.grid;
  }

  // Dedupe content for one emission: same list, same items, same event name. The
  // name is part of it so a layout switch re-fires the list under the new name,
  // while an unchanged view still cannot fire twice.
  function gxListSignature(name, items) {
    return (
      name +
      '|' +
      items
        .map(function (item) {
          return item.item_id;
        })
        .join(',')
    );
  }

  // Groups the per-card select_item payloads into one view_item_list per list
  // (keyed by item_list_id or section id). Each list fires when its first card
  // scrolls into the viewport. Safe to call again after a DOM injection: cards
  // counted by an earlier scan are skipped, so only newly added cards can form
  // a group.
  function fireViewItemLists() {
    // Also runs after every injected-list scan, which is when a facet render has
    // put a fresh #ProductsList in place that needs watching again.
    observeCollectionLayout();
    var cards = document.querySelectorAll(GX_CARD_SEL);
    if (!cards.length) return;
    var groups = {};
    var order = [];
    Array.prototype.forEach.call(cards, function (card) {
      if (card._gxListed) return;
      // Swiper's loop mode clones slides (cloneNode drops _gxListed), so clones
      // would look like a fresh list. They are never counted.
      if (card.closest && card.closest('.swiper-slide-duplicate')) return;
      card._gxListed = true;
      var payload = ecPayload(card);
      if (!payload.items || !payload.items.length) return;
      var section = card.closest ? card.closest('[id^="shopify-section"]') : null;
      var key = payload.item_list_id || (section ? section.id : 'list');
      if (!groups[key]) {
        groups[key] = {
          item_list_id: payload.item_list_id || '',
          item_list_name: payload.item_list_name || '',
          items: [],
          // The list's first card is the in-view trigger.
          container: card
        };
        order.push(key);
      }
      groups[key].items = groups[key].items.concat(payload.items);
    });

    function emit(key) {
      var g = groups[key];
      if (!g || g._fired) return;
      g._fired = true;
      // Same list with the same items must not re-fire: predictive search
      // re-renders its results per keystroke, and clearing a facet can restore
      // the previous result set. A changed set (infinite scroll, other filter)
      // does fire.
      var name = gxListEventName(g.container);
      var signature = gxListSignature(name, g.items);
      if (gxFiredLists[key] === signature) return;
      gxFiredLists[key] = signature;
      trackEcommerce(name, {
        item_list_id: g.item_list_id,
        item_list_name: g.item_list_name,
        items: g.items
      });
    }

    if (!('IntersectionObserver' in window)) {
      order.forEach(emit); // no IntersectionObserver: fire all immediately
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          observer.unobserve(el);
          (el._gxListKeys || []).forEach(emit);
          el._gxListKeys = null;
        });
      },
      { threshold: 0.5 }
    );

    // A single container can host several lists; collect all its keys before
    // observing it once.
    var containers = [];
    order.forEach(function (key) {
      var el = groups[key].container;
      if (!el._gxListKeys) {
        el._gxListKeys = [];
        containers.push(el);
      }
      el._gxListKeys.push(key);
    });
    containers.forEach(function (el) {
      observer.observe(el);
    });
  }

  // Re-emits the collection grid's list after a layout switch. The rendered
  // cards do not change - only the view, and therefore the event name - so the
  // payload is rebuilt from the cards currently in the grid.
  function fireCollectionListView() {
    var grid = gxCollectionGrid();
    if (!grid) return;
    var cards = grid.querySelectorAll(GX_CARD_SEL);
    if (!cards.length) return;

    var items = [];
    var listId = '';
    var listName = '';
    Array.prototype.forEach.call(cards, function (card) {
      var payload = ecPayload(card);
      if (!payload.items || !payload.items.length) return;
      if (!listId) listId = payload.item_list_id || '';
      if (!listName) listName = payload.item_list_name || '';
      items = items.concat(payload.items);
    });
    if (!items.length) return;

    // Same key as fireViewItemLists, so the two paths share one dedupe slot and
    // cannot emit twice for the same view.
    var section = grid.closest ? grid.closest('[id^="shopify-section"]') : null;
    var key = listId || (section ? section.id : 'list');
    // Nothing emitted for this list yet: the grid has not scrolled into view, so
    // the intersection scan still owns the first event and will already pick the
    // name matching the current layout.
    if (!(key in gxFiredLists)) return;

    var name = gxListEventName(grid);
    var signature = gxListSignature(name, items);
    if (gxFiredLists[key] === signature) return;
    gxFiredLists[key] = signature;
    trackEcommerce(name, { item_list_id: listId, item_list_name: listName, items: items });
  }

  // Watches the layout attribute rather than the switcher buttons, so every path
  // that changes the view is covered: a button click, the localStorage restore on
  // load, and the re-apply after a facet render (assets/facets.js). Re-attaches
  // per grid element, because a facet render replaces #ProductsList.
  function observeCollectionLayout() {
    var grid = gxCollectionGrid();
    if (!grid || grid._gxLayoutObserved || !('MutationObserver' in window)) return;
    grid._gxLayoutObserved = true;
    var last = grid.dataset.layout || 'grid';
    new MutationObserver(function () {
      var layout = grid.dataset.layout || 'grid';
      if (layout === last) return;
      last = layout;
      fireCollectionListView();
    }).observe(grid, { attributes: true, attributeFilter: ['data-layout'] });
  }

  // Lists injected after load (recommendations, recently viewed, predictive
  // search, facet/pagination re-render) are not in the initial DOM, so they need
  // a re-scan. One debounced scan per burst of injected cards.
  var gxListScanTimer = null;
  function scheduleViewItemListScan() {
    if (gxListScanTimer) return;
    gxListScanTimer = setTimeout(function () {
      gxListScanTimer = null;
      fireViewItemLists();
    }, 250);
  }

  // Watches for injected product cards and triggers the re-scan.
  function observeInjectedLists() {
    if (!('MutationObserver' in window) || !document.body) return;
    var observer = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i += 1) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j += 1) {
          var node = added[j];
          if (!node || node.nodeType !== 1) continue;
          if (
            (node.matches && node.matches(GX_CARD_SEL)) ||
            (node.querySelector && node.querySelector(GX_CARD_SEL))
          ) {
            scheduleViewItemListScan();
            return;
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function initEcommerce() {
    var ecCfg = (window.BritaGxConfig || {}).ecommerce;
    if (Array.isArray(ecCfg)) {
      ecCfg.forEach(function (entry) {
        if (entry && entry.event) trackEcommerce(entry.event, entry.ecommerce || {});
      });
    }

    fireViewItemLists();
    observeInjectedLists();

    // Seed the cart snapshot as the baseline for the first add/remove diff, and
    // sync the durable list map against the cart this page load starts with (no
    // diff here - the baseline must not emit events). A prune or a mirror entry
    // the server does not have yet is written; an unchanged map costs no request.
    fetchCart(function (cart) {
      if (lineAttrEnabled()) {
        lineAttrAdopt(cart);
        lineAttrPrune(cart);
        lineAttrSchedule();
      }
      if (!GxEc.snapshot) GxEc.snapshot = indexCart(cart);
    });

    // add_to_cart / remove_from_cart from the theme `cart:updated` bus.
    document.addEventListener('cart:updated', function (ev) {
      var cart = ev && ev.detail && ev.detail.cart;
      if (cart && cart.items) handleCart(cart);
    });

    // view_cart when the cart drawer opens.
    document.addEventListener('drawer:handleAfterShow', function (ev) {
      var t = ev.target;
      if (!t || (t.tagName || '').toLowerCase() !== 'cart-drawer') return;
      fetchCart(fireViewCart);
    });
  }

  // ---- scroll: depth milestones, once per page load ----------------------
  // Fires `scroll` with percentage_milestone '25' | '50' | '75' | '90' | '100'
  // (string) and the page_type from page_meta. The depth is the bottom edge of
  // the viewport relative to the full document height. Only real scrolling
  // counts: a page that fits the viewport never scrolls, so it reports nothing
  // instead of firing every milestone on load. A jump to the bottom reports all
  // milestones passed, in order.
  var GX_SCROLL_MILESTONES = [25, 50, 75, 90, 100];
  var gxScrollFired = {};
  var gxScrollTicking = false;

  function gxScrollCheck() {
    gxScrollTicking = false;
    var doc = document.documentElement;
    var total = Math.max(doc.scrollHeight, document.body ? document.body.scrollHeight : 0);
    if (!total) return;
    var viewed = (window.pageYOffset || doc.scrollTop || 0) + window.innerHeight;
    var percent = (viewed / total) * 100;
    GX_SCROLL_MILESTONES.forEach(function (m) {
      // 100 tolerates sub-pixel rounding at the very bottom.
      var reached = m === 100 ? viewed >= total - 2 : percent >= m;
      if (!reached || gxScrollFired[m]) return;
      gxScrollFired[m] = true;
      track('scroll', { percentage_milestone: String(m), page_type: currentPageType() });
    });
    // Everything reported: stop listening for the rest of the page load.
    if (gxScrollFired[100]) window.removeEventListener('scroll', gxScrollOnScroll);
  }

  function gxScrollOnScroll() {
    if (gxScrollTicking) return;
    gxScrollTicking = true;
    window.requestAnimationFrame(gxScrollCheck);
  }

  window.addEventListener('scroll', gxScrollOnScroll, { passive: true });

  // ---- Public API -------------------------------------------------------

  window.BritaGx = {
    track: track,
    trackEcommerce: trackEcommerce,
    collectParams: collectParams,
    // Called by the carousel hook in theme.js after a slide change, so a
    // promotion that was hidden behind another slide can still fire its
    // view_promotion.
    refreshPromotionViews: recheckPendingPromotions
  };

  // ---- page_meta: first event on every page -----------------------------
  // Deferred to DOMContentLoaded (like initEcommerce/eec.view_item below) so
  // page_meta and the PDP's eec.view_item only reach the dataLayer once the
  // DOM is actually ready, not while the script is still being parsed.
  function initOnLoad() {
    var cfg = window.BritaGxConfig || {};
    if (cfg.page_meta) {
      track('page_meta', cfg.page_meta);
    }

    // Config-driven one-shot events (e.g. newsletter_doi confirmation landing).
    if (Array.isArray(cfg.autofire)) {
      cfg.autofire.forEach(function (item) {
        if (item && item.event) track(item.event, item.payload || {});
      });
    }

    // view_search_results: bare event name with a flat `search_term`.
    if (cfg.search_term) {
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'view_search_results', search_term: cfg.search_term });
      } catch (e) {
        /* ignore */
      }
    }

    // Storefront ecommerce init (runs after page_meta) - includes the PDP's
    // eec.view_item via BritaGxConfig.ecommerce.
    initEcommerce();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initOnLoad);
  } else {
    initOnLoad();
  }
})();
